import { Button, Checkbox } from "@/components/ui";
import type {
  DashboardActionColumn,
  DashboardDataColumn,
} from "@/lib/types/MatterDashboardTable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

export function DashboardSortableActionRow({
  action,
  checked,
  onCheckedChange,
  phaseTitles,
  reorderable = true,
}: {
  action: DashboardActionColumn | DashboardDataColumn;
  checked: boolean;
  onCheckedChange: () => void;
  phaseTitles?: string[];
  reorderable?: boolean;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: action.key, disabled: !reorderable });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`bg-card flex min-h-11 items-center gap-3 px-2 py-1 ${isDragging ? "relative z-10 opacity-60" : "hover:bg-muted/50"}`}
    >
      {reorderable && (
        <Button
          ref={setActivatorNodeRef}
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground size-8 shrink-0 cursor-grab active:cursor-grabbing"
          aria-label={`Drag to reorder ${action.title}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden="true" />
        </Button>
      )}
      <Checkbox
        aria-label={`${reorderable ? "Show action" : "Include action"}: ${action.title}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      <span className="min-w-0 flex-1 text-sm">
        <span className="block truncate" title={action.title}>
          {action.title}
        </span>
        {phaseTitles && phaseTitles.length > 0 && (
          <span
            className="text-muted-foreground block truncate text-xs"
            title={phaseTitles.join(", ")}
          >
            {phaseTitles.join(", ")}
          </span>
        )}
      </span>
      {action.kind === "matterData" && (
        <span className="text-muted-foreground text-xs">Matter data</span>
      )}
      {(action.lifecycle === "Retired" ||
        (action.kind === "matterData" && !action.available)) && (
        <span className="text-muted-foreground text-xs">Unavailable</span>
      )}
    </div>
  );
}
